import {announce} from '@github-ui/aria-live'
import {Banner, FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type ChangeEvent, type FormEvent, type RefObject, useCallback, useId, useRef, useState} from 'react'

import {getDefaultArtifactLinkName, isValidArtifactUrl} from '../artifact-url'
import styles from './CreateLinkDialog.module.css'

const INVALID_LINK_MESSAGE = 'Enter a valid URL that starts with http:// or https://.'

export type LinkArtifactInput = {
  name: string
  body: string
}

export type CreateLinkDialogProps = {
  artifact?: LinkArtifactInput
  errorMessage?: string
  isPending: boolean
  onClose: () => void
  onInputChange?: () => void
  onSubmit: (input: LinkArtifactInput) => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export function CreateLinkDialog({
  artifact,
  errorMessage,
  isPending,
  onClose,
  onInputChange,
  onSubmit,
  returnFocusRef,
}: CreateLinkDialogProps) {
  const formId = useId()
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState(artifact?.name ?? '')
  const [url, setUrl] = useState(artifact?.body ?? '')
  const [urlValidationError, setUrlValidationError] = useState('')
  const isEditing = artifact !== undefined
  const dialogTitle = isEditing ? 'Edit link' : 'Add link'
  const urlIsEmpty = url.trim().length === 0

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTitle(event.currentTarget.value)
      onInputChange?.()
    },
    [onInputChange],
  )

  const handleUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.currentTarget.value)
      setUrlValidationError('')
      onInputChange?.()
    },
    [onInputChange],
  )

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (urlIsEmpty) return

      const trimmedUrl = url.trim()
      if (!isValidArtifactUrl(trimmedUrl)) {
        setUrlValidationError(INVALID_LINK_MESSAGE)
        announce(INVALID_LINK_MESSAGE, {assertive: true})
        return
      }

      onSubmit({
        name: title.trim() || getDefaultArtifactLinkName(trimmedUrl),
        body: trimmedUrl,
      })
    },
    [onSubmit, title, url, urlIsEmpty],
  )

  return (
    <Dialog
      title={dialogTitle}
      className={clsx(styles.CreateDialog, errorMessage && styles.CreateDialogHasBanner)}
      width="large"
      height="auto"
      onClose={onClose}
      initialFocusRef={titleInputRef}
      returnFocusRef={returnFocusRef}
      footerButtons={[
        {content: 'Cancel', onClick: onClose, buttonType: 'normal', disabled: isPending},
        {
          content: isEditing ? 'Save changes' : 'Add link',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: urlIsEmpty || isPending,
          loading: isPending,
          loadingAnnouncement: isEditing ? 'Saving link' : 'Adding link',
        },
      ]}
    >
      <form id={formId} aria-label={dialogTitle} onSubmit={handleSubmit} className={styles.Form} noValidate>
        {errorMessage ? (
          <Banner
            title={isEditing ? 'Unable to update link' : 'Unable to add link'}
            variant="critical"
            description={errorMessage}
          />
        ) : null}
        <FormControl disabled={isPending}>
          <FormControl.Label>Link title</FormControl.Label>
          <TextInput ref={titleInputRef} value={title} block onChange={handleTitleChange} />
        </FormControl>
        <FormControl required disabled={isPending}>
          <FormControl.Label requiredText=" (required)">Link</FormControl.Label>
          <TextInput
            type="url"
            value={url}
            block
            aria-invalid={urlValidationError ? true : undefined}
            onChange={handleUrlChange}
          />
          <FormControl.Caption>Use a full URL, like https://github.com/owner/repo.</FormControl.Caption>
          {urlValidationError ? (
            <FormControl.Validation variant="error">{urlValidationError}</FormControl.Validation>
          ) : null}
        </FormControl>
      </form>
    </Dialog>
  )
}
